import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import VesselMsiValue from './VesselMsiValue';
import { IValueVessel, TVesselListMsiValue, TVesselListRecentSales } from '../valueTypes';
import { formatValueAsString } from '../../../utils/helpers';
import InfoNote from '../../common/SharedComponents/Info-Note';
import { themeBalham } from 'ag-grid-enterprise';
import DynamicTab from '../../../shared/components/tabs/DynamicTab';
import { useMemo } from 'react';
import ImoAndNameCellRenderer from '../../../shared/components/renderers/customCellRenderers/ImoAndNameCellRenderer';
import OwnerCellRenderer from '../../../shared/components/renderers/customCellRenderers/OwnerCellRenderer';

export interface IFleetMsiValue {
	vesselListMsiValue: TVesselListMsiValue | null;
	vesselListRecentSales: TVesselListRecentSales | null;
	selectedVessels: IValueVessel[] | [];
}

const FleetMsiValue: React.FC<IFleetMsiValue> = ({ ...props }) => {
	interface ISummaryItem {
		imoNumber: string;
		vesselName: string;
		sector: string;
		size: number | null;
		sizeUnit: string;
		built: string;
		owner: string;
		gearTankCargoContainment: string;
		fuelSystem: string;
		fairMarketValue: number;
	}

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			flex: 1,
			filter: 'agTextColumnFilter',
		};
	}, []);

	const colDefs: ColDef<ISummaryItem>[] = [
		{
			headerName: '# Name',
			field: 'vesselName',
			floatingFilter: true,
			cellStyle: (params) =>
				params.value === 'Total'
					? { backgroundColor: '#eeeeee', fontWeight: 'bold', color: 'black' }
					: { backgroundColor: '#eeeeee', fontWeight: 'normal', color: 'black' },
			aggFunc: () => 'Total',
			headerTooltip: '# Name',
			cellRenderer: ImoAndNameCellRenderer,
			cellRendererParams: {
				// This is where you can pass additional parameters to the cell renderer if needed
				imoNumber: (params: any) => params.data.imoNumber,
			},
		},
		// { headerName: 'IMO Number', field: 'imoNumber', floatingFilter: true, headerTooltip: 'IMO Number' },
		{
			headerName: 'IMO Number',
			field: 'imoNumber',
			floatingFilter: true,
			headerTooltip: 'IMO Number',
			cellRenderer: ImoAndNameCellRenderer,
			cellRendererParams: {
				// This is where you can pass additional parameters to the cell renderer if needed
				imoNumber: (params: any) => params.data.imoNumber,
			},
		},
		{ headerName: 'Sector', field: 'sector', filter: 'agSetColumnFilter', floatingFilter: true, headerTooltip: 'Sector' },
		{
			headerName: 'Size',
			field: 'size',
			floatingFilter: true,
			headerTooltip: 'Size',
			valueFormatter: (params: any) =>
				`${formatValueAsString(params.data?.size, { minimumFractionDigits: 0, maximumFractionDigits: 2 }, '', '', ' (' + params.data?.sizeUnit + ')')}`,
		},
		{ headerName: 'Built', field: 'built', floatingFilter: true, headerTooltip: 'Built' },
		{ headerName: 'Owner', field: 'owner', floatingFilter: true, headerTooltip: 'Owner', cellRenderer: OwnerCellRenderer },
		{
			headerName: 'Gear / Tank',
			field: 'gearTankCargoContainment',
			floatingFilter: true,
			headerTooltip: 'Gear / Tank',
		},
		{ headerName: 'Fuel System', field: 'fuelSystem', floatingFilter: true, headerTooltip: 'Fuel System' },
		{
			headerName: 'Current Value ($ Mn)',
			field: 'fairMarketValue',
			cellStyle: { backgroundColor: '#3785BF', color: 'white' },
			floatingFilter: true,
			headerTooltip: 'Current Value ($ Mn)',
			valueFormatter: (params: any) =>
				formatValueAsString(params.value, { minimumFractionDigits: 0, maximumFractionDigits: 1 }, '', '$', ' Mn'),

			aggFunc: () => {
				const totalFairMarketValue = data.reduce(
					(sum, vessel) => sum + (!Number.isNaN(vessel.fairMarketValue) ? vessel.fairMarketValue : 0),
					0,
				);
				return totalFairMarketValue;
			},
		},
	];

	const data: ISummaryItem[] = props.selectedVessels.map((vessel) => ({
		vesselName: vessel.name,
		imoNumber: vessel.imoNumber.toString(),
		sector: vessel.sectorName,
		size: vessel.size,
		sizeUnit: vessel.sizeUnit,
		built: vessel.yearOfDelivery.toString(),
		owner: vessel.owner,
		gearTankCargoContainment: vessel.gearTankCargoContainment,
		fuelSystem: vessel.fuelSystem,
		fairMarketValue: Number(
			props.vesselListMsiValue?.dataList?.find((v) => v.imoNumber === vessel.imoNumber)?.summary?.fairMarketValue ?? 0,
		),
	}));

	const rowData = data;

	return (
		<DynamicTab
			dynamicItem={VesselMsiValue}
			dynamicItemProps={(item: any) => ({
				vesselMsiValue:
					props.vesselListMsiValue?.dataList?.length || 0 > 0
						? props.vesselListMsiValue?.dataList?.find((v) => v.imoNumber === item.imoNumber) || null
						: null,
				data: item,
				giveSpace: false,
				isLocalMode: true,
				vesselRecentSales:
					props.vesselListRecentSales?.dataList?.length || 0 > 0
						? props.vesselListRecentSales?.dataList?.find((v) => v.imoNumber === item.imoNumber)?.vesselRecentSales || []
						: [],
			})}
			dynamicItemData={props.selectedVessels}
			staticItems={[
				<div className="flex flex-col w-full h-full">
					<div className="w-full h-full">
						<AgGridReact
							columnDefs={colDefs}
							rowData={rowData}
							defaultColDef={defaultColDef}
							theme={themeBalham}
							grandTotalRow={'bottom'}
						/>
					</div>
					<div className="mt-4">
						<InfoNote />
					</div>
				</div>,
			]}
			tabColor="msi-midblue-color-500"
			giveSpace					
		/>
	);
};

export default FleetMsiValue;
